<template>
  <transition name="fade">
    <section
      v-if="products.length"
      :id="`rebuy-complete-the-look-${widgetId}`"
      class="rebuy-complete-the-look"
      :class="{
        [$class('hidden')]: hideBeforeUnmount
      }"
    >
      <div class="rebuy-complete-the-look__header">
        <h3
          v-if="title"
          class="text-body-m-medium rebuy-complete-the-look__title"
          v-text="title"
        />

        <div
          v-if="splideOverflow"
          class="rebuy-complete-the-look__controls is-desktop"
        >
          <button
            type="button"
            class="splide__arrow splide__arrow--prev"
            :aria-label="$string('accessibility.carousel.previous')"
            :disabled="splideIndex === 0"
            @click="$refs.splide.go('<')"
          >
            <icon-arrow-left />
          </button>

          <button
            type="button"
            class="splide__arrow splide__arrow--next"
            :aria-label="$string('accessibility.carousel.next')"
            :disabled="splideIndex === products.length - 1"
            @click="$refs.splide.go('>')"
          >
            <icon-arrow-right />
          </button>
        </div>
      </div>

      <splide
        ref="splide"
        class="rebuy-complete-the-look__items"
        :has-track="false"
        :aria-label="title"
        :options="splideOptions"
        @splide:moved="handleSplideMove"
        @splide:resized="checkOverflow"
      >
        <splide-track>
          <splide-slide
            v-for="product of products"
            :key="`complete-look-${product.id}`"
          >
            <product-card
              :image-options="{
                sizes: '168px',
                minMax: '168-336'
              }"
              :product="product"
              source="rebuy_complete_look"
            />
          </splide-slide>
        </splide-track>
      </splide>
    </section>
  </transition>

  <use-rebuy
    :key="productId"
    :widget-id="widgetId"
    :additional-data="{
      'data-rebuy-shopify-product-ids': productId
    }"
    @products="updateProducts"
    @title="payload => title = payload"
  />
</template>

<script>

/**
 * Vue: Rebuy complete the look (rebuy-complete-the-look)
 * -----------------------------------------------------------------------------
 * A complete the look carousel powered by Rebuy. Used on the product page.
 *
 * @param {String|Number} productId - The product ID of the product that
 * Rebuy should find related products for
 * @param {String|Number} widgetId - The Rebuy widget ID
 */

import {
  Splide,
  SplideSlide,
  SplideTrack,
} from '@splidejs/vue-splide'

import ProductCard from '~async/product-card/product-card'
import UseRebuy from '~async/use-rebuy/use-rebuy'

import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'

import { track } from '~/helpers/datalayer-actions'
import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'RebuyCompleteTheLook',
  components: {
    IconArrowRight,
    IconArrowLeft,
    ProductCard,
    Splide,
    SplideSlide,
    SplideTrack,
    UseRebuy,
  },

  props: {
    productId: {
      type: [String, Number],
      default: '',
      required: true,
    },
    widgetId: {
      type: [String, Number],
      default: '',
      required: true,
    },
  },

  data() {
    return {
      analytics: {
        widgetId: this.widgetId,
        widgetSource: 'PDP',
      },
      products: [],
      title: '',
      splideOptions: {
        flickMaxPages: 1,
        focus: 'left',
        wheel: false,
        arrows: false,
        drag: true,
        flickPower: 50,
        pagination: true,
        autoplay: false,
        respectReducedMotion: true,
        gap: '1rem',
        padding: {
          right: 'var(--spacing-ml)',
        },
        mediaQuery: 'min',
        omitEnd: true,
        fixedWidth: '168px',
        breakpoints: {
          [breakpoints.l]: {
            pagination: false,
          },
        },
      },
      splideIndex: 0,
      splideOverflow: false,
      hideBeforeUnmount: false,
      breakpoints,
    }
  },

  watch: {

    /**
     * Check if splide is overflowing whenever products update
     */
    products() {
      this.checkOverflow()
    },
  },

  methods: {

    /**
     * Check if splide is overflowing
     */
    async checkOverflow() {
      await this.$nextTick()
      this.splideOverflow = this.$refs.splide?.$el?.classList.contains('is-overflow')
    },

    /**
     * Track click when Splide moves and update arrows
     */
    async handleSplideMove(splide) {
      track({
        event: 'rebuy_carousel_click',
        ...this.analytics,
      })

      /**
       * Handle show / hide arrows
       * Because of the fixed widths, splide doesn't detect when the end of
       * the carousel has been reached
       */
      await this.$nextTick()
      const slides = splide.Components.Slides.get().filter(component => {
        return component.slide.classList.contains(this.$class('visible'))
      })
      const lastIndex = slides.length - 1
      const startSlide = slides[0]?.index === 0 && slides[0]
      const endSlide = slides[lastIndex]?.index === this.products.length - 1 &&
        slides[lastIndex]

      if (startSlide) {
        this.splideIndex = startSlide.index
      } else if (endSlide) {
        this.splideIndex = endSlide.index
      } else {
        this.splideIndex = -1
      }
    },

    /**
     * Update products and filter out the current product
     * @param payload - Products payload from use-rebuy component
     */
    async updateProducts(payload) {
      const products = payload.filter(product => {
        return product.id !== this.productId
      })

      /**
       * Splide gets unmounted with the `v-if` products.length check.
       * Splide has an issue where, when unmounting with animations, it
       * unmounts before the animation finishes.
       *
       * We want to use v-if so the widgets that have 0 products don't
       * unnecessarily render Splide instances.
       */
      if (!products.length) {
        this.hideBeforeUnmount = true
        await this.$nextTick()
      }

      this.products = products

      await this.$nextTick()
      this.hideBeforeUnmount = false
    },
  },
}
</script>

<style lang="scss">
@import './rebuy-complete-the-look';
</style>
